import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  CheckCheck,
  ChefHat,
  Clock3,
  RefreshCw,
  UtensilsCrossed,
} from "lucide-react";
import { api, ApiRequestError, type Me } from "../lib/api";
import type { OrderView, StaffOrdersResponse } from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { useRealtime } from "../lib/realtime";

function elapsedLabel(createdAt: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60_000));
  if (minutes < 1) return "baru saja";
  if (minutes < 60) return `${minutes} menit`;
  return `${Math.floor(minutes / 60)} jam ${minutes % 60} mnt`;
}

function beep(context: AudioContext) {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.value = 760;
  gain.gain.value = 0.08;
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.2);
  window.setTimeout(() => {
    const second = context.createOscillator();
    const secondGain = context.createGain();
    second.type = "triangle";
    second.frequency.value = 980;
    secondGain.gain.value = 0.08;
    second.connect(secondGain);
    secondGain.connect(context.destination);
    second.start();
    second.stop(context.currentTime + 0.18);
  }, 220);
}

const kdsStatusLabels: Record<string, string> = {
  queued: "Baru",
  preparing: "Dimasak",
  ready: "Siap",
  served: "Disajikan",
  completed: "Selesai",
  cancelled: "Dibatalkan",
};

export function KitchenPage({ me }: { me: Me }) {
  const [orders, setOrders] = useState<OrderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [soundOn, setSoundOn] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const knownIds = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const response = await api<StaffOrdersResponse>("/api/staff/orders");
      setOrders(
        response.orders.filter((order) =>
          ["queued", "preparing", "ready"].includes(order.status),
        ),
      );
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat antrean dapur.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const realtime = useRealtime({
    staff: true,
    onEvent: () => {
      void load();
    },
  });

  // Fallback lambat hanya saat koneksi realtime terputus.
  useEffect(() => {
    void load();
    if (realtime.connected) return;
    const timer = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(timer);
  }, [load, realtime.connected]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const currentIds = new Set(orders.map((order) => order.id));
    const newOrders = [...currentIds].filter((id) => !knownIds.current.has(id));
    if (knownIds.current.size > 0 && newOrders.length > 0 && soundOn && audioRef.current) {
      beep(audioRef.current);
    }
    knownIds.current = currentIds;
  }, [orders, soundOn]);

  function toggleSound() {
    if (soundOn) {
      setSoundOn(false);
      return;
    }
    try {
      audioRef.current = new AudioContext();
      void audioRef.current.resume();
      setSoundOn(true);
    } catch {
      setSoundOn(false);
    }
  }

  async function updateItem(order: OrderView, itemId: number, status: string) {
    const key = `item:${order.id}:${itemId}`;
    setBusyKey(key);
    try {
      const response = await api<{ order: OrderView }>(
        `/api/staff/orders/${order.id}/items/${itemId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
      );
      setOrders((current) =>
        current.map((row) => (row.id === order.id ? response.order : row)),
      );
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal memperbarui item.",
      );
    } finally {
      setBusyKey(null);
    }
  }

  async function updateOrder(order: OrderView, status: string) {
    const key = `order:${order.id}`;
    setBusyKey(key);
    try {
      const response = await api<{ order: OrderView }>(
        `/api/staff/orders/${order.id}/status`,
        { method: "PATCH", body: JSON.stringify({ status }) },
      );
      setOrders((current) =>
        current
          .map((row) => (row.id === order.id ? response.order : row))
          .filter((row) => !["served", "completed", "cancelled"].includes(row.status)),
      );
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal memperbarui pesanan.",
      );
    } finally {
      setBusyKey(null);
    }
  }

  const counts = useMemo(() => {
    return {
      queued: orders.filter((order) => order.status === "queued").length,
      preparing: orders.filter((order) => order.status === "preparing").length,
      ready: orders.filter((order) => order.status === "ready").length,
    };
  }, [orders]);

  const canKitchen = ["owner", "admin", "supervisor", "kitchen"].includes(
    me.membership.role,
  );
  const canServe = ["owner", "admin", "supervisor", "waiter"].includes(
    me.membership.role,
  );

  return (
    <div className="page-stack kds-page">
      <PageHeader
        eyebrow="Kitchen display"
        title="Antrean dapur"
        description="Pesanan yang sudah dibayar. Tandai item saat mulai dimasak dan saat siap diantar."
        actions={
          <div className="kds-actions">
            <RealtimeBadge connected={realtime.connected} />
            <button
              type="button"
              className={`btn ${soundOn ? "btn-secondary" : "btn-primary"}`}
              onClick={toggleSound}
            >
              {soundOn ? <Bell size={16} /> : <BellOff size={16} />}
              {soundOn ? "Suara aktif" : "Aktifkan suara"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => void load()}>
              <RefreshCw size={16} /> Muat ulang
            </button>
          </div>
        }
      />

      <div className="kds-stats">
        <div><span>Baru</span><strong>{counts.queued}</strong></div>
        <div><span>Dimasak</span><strong>{counts.preparing}</strong></div>
        <div><span>Siap</span><strong>{counts.ready}</strong></div>
        <div><span>Total aktif</span><strong>{orders.length}</strong></div>
      </div>

      {error ? <div className="alert alert--danger">{error}</div> : null}

      {loading ? (
        <div className="empty-state empty-state--roomy">
          <ChefHat size={32} />
          <h3>Memuat antrean...</h3>
        </div>
      ) : orders.length === 0 ? (
        <div className="empty-state empty-state--roomy">
          <UtensilsCrossed size={36} />
          <h3>Belum ada pesanan</h3>
          <p>Pesanan baru akan muncul di sini otomatis setelah pembayaran diterima.</p>
        </div>
      ) : (
        <div className="kds-grid">
          {orders.map((order) => {
            const isNew =
              now - new Date(order.createdAt).getTime() < 45_000 &&
              order.status === "queued";
            const activeItems = order.items.filter((item) => item.status !== "void");
            const allReady =
              activeItems.length > 0 &&
              activeItems.every((item) => item.status === "ready" || item.status === "served");
            return (
              <article
                key={order.id}
                className={`kds-card${isNew ? " is-new" : ""}`}
              >
                <header className="kds-card__head">
                  <div>
                    <strong>
                      {order.orderNo ? `#${order.orderNo}` : "Pesanan"}
                    </strong>
                    <span>{order.table?.label ?? "Tanpa meja"}</span>
                  </div>
                  <div className="kds-card__meta">
                    <span className={`item-status item-status--${order.status}`}>
                      {kdsStatusLabels[order.status] ?? order.status}
                    </span>
                    <span className="kds-card__time">
                      <Clock3 size={13} /> {elapsedLabel(order.createdAt, now)}
                    </span>
                  </div>
                </header>

                {order.customerPhone ? (
                  <p className="kds-card__phone">WA {order.customerPhone}</p>
                ) : null}
                <ul className="kds-items">
                  {activeItems.map((item) => (
                    <li key={item.id} className={`kds-item is-${item.status}`}>
                      <div className="kds-item__main">
                        <span className="kds-item__qty">{item.qty}×</span>
                        <div>
                          <strong>{item.name}</strong>
                          {item.notes ? <span className="kds-item__note">“{item.notes}”</span> : null}
                          {Array.isArray(item.modifiers) && item.modifiers.length > 0 ? (
                            <span className="kds-item__note">
                              {(item.modifiers as Array<{ name: string }>)
                                .map((modifier) => modifier.name)
                                .join(", ")}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="kds-item__actions">
                        {canKitchen && item.status === "queued" ? (
                          <button
                            type="button"
                            className="btn btn-primary btn--sm"
                            disabled={busyKey === `item:${order.id}:${item.id}`}
                            onClick={() => void updateItem(order, item.id, "preparing")}
                          >
                            Mulai
                          </button>
                        ) : null}
                        {canKitchen && item.status === "preparing" ? (
                          <button
                            type="button"
                            className="btn btn-primary btn--sm"
                            disabled={busyKey === `item:${order.id}:${item.id}`}
                            onClick={() => void updateItem(order, item.id, "ready")}
                          >
                            Siap
                          </button>
                        ) : null}
                        {item.status === "ready" ? (
                          <span className="kds-ready-mark">Siap</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                {order.notes ? (
                  <p className="kds-card__notes">Catatan: {order.notes}</p>
                ) : null}

                <footer className="kds-card__foot">
                  {canKitchen && !allReady ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn--sm"
                      disabled={busyKey === `order:${order.id}`}
                      onClick={() => void updateOrder(order, "ready")}
                    >
                      <CheckCheck size={15} /> Semua siap
                    </button>
                  ) : null}
                  {canServe && order.status === "ready" ? (
                    <button
                      type="button"
                      className="btn btn-primary btn--sm"
                      disabled={busyKey === `order:${order.id}`}
                      onClick={() => void updateOrder(order, "served")}
                    >
                      Sudah disajikan
                    </button>
                  ) : null}
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
